import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, LayoutGrid, List as ListIcon, PlayCircle, Trash2, Shuffle } from 'lucide-react';
import { AudioFile } from '../types';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { useFavorites } from '../context/FavoritesContext';
import { useToast } from '../context/ToastContext';
import ConfirmationModal from '../components/common/ConfirmationModal';
import NaatCard from '../components/NaatCard';
import SEO from '../components/SEO';
import './FavoriteNaatsPage.css';

const FavoriteNaatsPage: React.FC = () => {
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const { state, playAudio, addToQueue, togglePlay } = useAudioPlayer();
  const { favorites, toggleFavorite, clearFavorites } = useFavorites();
  const { showToast } = useToast();

  const TOTAL_PAGES = Math.ceil(favorites.length / pageSize);

  // Calculate current page data
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageData = favorites.slice(startIndex, endIndex);

  const handlePlayAudio = (audio: AudioFile) => {
    // If this audio is already playing, just toggle play/pause
    if (state.currentAudio?.id === audio.id) {
      togglePlay();
    } else {
      addToQueue(audio);
      playAudio(audio, favorites, 'favorites');
    }
  };

  const handlePlayAll = () => {
    if (favorites.length > 0) {
      playAudio(favorites[0], favorites, 'favorites');
      showToast(`Playing all favorites (${favorites.length} naats)`, 'success', 3000, 'bottom-right');
    }
  };

  const handleShufflePlay = () => {
    if (favorites.length > 0) {
      // Create shuffled copy of favorites
      const shuffled = [...favorites].sort(() => Math.random() - 0.5);
      playAudio(shuffled[0], shuffled, 'favorites');
      showToast(`Shuffling favorites (${favorites.length} naats)`, 'success', 3000, 'bottom-right');
    }
  };

  const handleAddToQueue = (audio: AudioFile) => {
    const added = addToQueue(audio);
    if (added) {
      showToast(`Added "${audio.title}" to queue`, 'success', 3000, 'bottom-right');
    } else {
      showToast(`"${audio.title}" is already in queue`, 'info', 3000, 'bottom-right');
    }
  };

  const handleToggleFavorite = (audio: AudioFile) => {
    toggleFavorite(audio);
    showToast(`Removed "${audio.title}" from favorites`, 'info', 3000, 'top-right-favorites');
  };

  const handleDownloadComplete = (audio: AudioFile, success: boolean) => {
    if (success) {
      showToast(`Downloaded "${audio.title}" successfully! 🎵`, 'success', 4000, 'bottom-right');
    } else {
      showToast(`Failed to download "${audio.title}". Please try again.`, 'error', 4000, 'bottom-right');
    }
  };

  const handleClearFavorites = () => {
    setShowClearConfirmation(true);
  };

  const confirmClearFavorites = () => {
    clearFavorites();
    setCurrentPage(1);
    setShowClearConfirmation(false);
    showToast('All favorites cleared', 'info', 3000, 'bottom-right');
  };

  const handleLayoutChange = (mode: 'grid' | 'list') => {
    setLayout(mode);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Check if current audio is a favorite and playing
  const isCurrentlyPlaying = (audio: AudioFile): boolean => {
    return state.currentAudio?.id === audio.id && state.isPlaying;
  };

  return (
    <div className="favorite-naats-page">
      <SEO
        title="My Favorites - NaatCity"
        description="Access your personalized collection of favorite Islamic Naats on NaatCity. Listen to your handpicked naats and manage your spiritual audio playlist."
        keywords="favorite naats, my naats collection, saved naats, personal playlist, bookmarked naats"
        url="https://naatcity.com/favorites"
      />
      <div className="page-header">
        <div className="header-content">
          <div className="header-center">
            <h1>
              <Star size={32} />
              My Favorites
            </h1>
            <div className="results-count">
              {favorites.length > 0 ? (
                <>
                  {currentPageData.length > 0 ? (
                    <>Showing {startIndex + 1}-{Math.min(endIndex, favorites.length)} of {favorites.length} Favorite Naats</>
                  ) : (
                    `${favorites.length} Favorite Naats`
                  )}
                </>
              ) : (
                'No Favorite Naats'
              )}
            </div>
          </div>
          <div className="layout-toggle" role="group" aria-label="Change layout">
            <button
              type="button"
              className={`layout-btn ${layout === 'grid' ? 'active' : ''}`}
              aria-pressed={layout === 'grid'}
              onClick={() => handleLayoutChange('grid')}
            >
              <LayoutGrid size={16} /> <span className="visually-hidden">Grid view</span>
            </button>
            <button
              type="button"
              className={`layout-btn ${layout === 'list' ? 'active' : ''}`}
              aria-pressed={layout === 'list'}
              onClick={() => handleLayoutChange('list')}
            >
              <ListIcon size={16} /> <span className="visually-hidden">List view</span>
            </button>
          </div>
        </div>

        {/* Playlist Controls */}
        {favorites.length > 0 && (
          <div className="playlist-controls">
            <button
              className="play-all-btn"
              onClick={handlePlayAll}
              title="Play all favorites in order"
            >
              <PlayCircle size={20} />
              <span>Play All</span>
            </button>
            <button
              className="shuffle-btn"
              onClick={handleShufflePlay}
              title="Shuffle and play favorites"
            >
              <Shuffle size={18} />
              <span>Shuffle</span>
            </button>
            <button
              className="clear-btn"
              onClick={handleClearFavorites}
              title="Clear all favorites"
            >
              <Trash2 size={18} />
              <span>Clear All</span>
            </button>
          </div>
        )}
      </div>

      <div className="content-section">
        {favorites.length === 0 ? (
          <div className="empty-state">
            <Star size={64} />
            <h3>No Favorite Naats Yet</h3>
            <p>Start adding naats to your favorites by clicking the heart icon on any naat.</p>
            <Link to="/popular" className="browse-link">
              Browse Popular Naats
            </Link>
          </div>
        ) : layout === 'list' ? (
          <div className="naat-card-list">
            {currentPageData.map((audio, index) => (
              <NaatCard
                key={audio.id}
                naat={audio}
                serialNumber={startIndex + index + 1}
                isPlaying={isCurrentlyPlaying(audio)}
                isFavorite={true}
                onPlay={handlePlayAudio}
                onPause={() => togglePlay()}
                onAddToQueue={handleAddToQueue}
                onToggleFavorite={handleToggleFavorite}
                onDownloadComplete={handleDownloadComplete}
              />
            ))}
          </div>
        ) : (
          <div className="naat-card-grid">
            {currentPageData.map((audio, index) => (
              <NaatCard
                key={audio.id}
                naat={audio}
                serialNumber={startIndex + index + 1}
                isPlaying={isCurrentlyPlaying(audio)}
                isFavorite={true}
                onPlay={handlePlayAudio}
                onPause={() => togglePlay()}
                onAddToQueue={handleAddToQueue}
                onToggleFavorite={handleToggleFavorite}
                onDownloadComplete={handleDownloadComplete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {favorites.length > pageSize && (
        <div className="bottom-controls">
          <div className="simple-pagination">
            {Array.from({ length: TOTAL_PAGES }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={`page-btn ${currentPage === page ? 'active' : ''}`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}
          </div>
          <div className="page-size-control">
            <label>Items per page:</label>
            <select value={pageSize} onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showClearConfirmation}
        title="Clear All Favorites"
        message="Are you sure you want to remove all favorites? This action cannot be undone."
        confirmText="Clear All"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmClearFavorites}
        onClose={() => setShowClearConfirmation(false)}
      />
    </div>
  );
};

export default FavoriteNaatsPage;
